import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { colors } from "../../styles/figmaColors";
import { CheckValidGreen } from "../../assets/assets";
import { Badge } from "./Tag/Badge";
import { Spacer } from "./Spacer";
import { TextCapitalized } from "./text/TextCapitalized";
import { RowElement } from "./RowElement/RowElement";
import { typographies } from "../../styles/figmaTypographies";
import "../../../i18n/i18n";

export type PendingActionTask = {
  icon?: JSX.Element;
  title: string;
  subtitle: string;
  button: JSX.Element;
};

export interface PendingActionsCardProps {
  subject: string;
  tasks: PendingActionTask[];
  maxWidth?: number;
}

export const PendingActionsCard = (props: PendingActionsCardProps) => {
  const { t } = useTranslation();
  const { subject, tasks, maxWidth } = props;
  const count = tasks.length;

  const emptyState = (
    <StyledEmpty>
      <Spacer y={2.5} />
      <StyledCheckCircle>
        <CheckValidGreen />
      </StyledCheckCircle>
      <Spacer y={1} />
      <StyledEmptyTitle>
        <TextCapitalized>{t("pendingActionsCard.empty.title")}</TextCapitalized>
      </StyledEmptyTitle>
      <Spacer y={0.5} />
      <StyledEmptyText>
        <TextCapitalized>
          {t("pendingActionsCard.empty.text", { subject })}
        </TextCapitalized>
      </StyledEmptyText>
      <Spacer y={2.5} />
    </StyledEmpty>
  );

  const taskRows = tasks.map((task, index) => (
    <StyledTask key={`task_${subject}_${index}`}>
      <RowElement
        size={"L"}
        leftIcon={task.icon}
        text={task.title}
        subText={task.subtitle}
        rightElement={<StyledNoWrap>{task.button}</StyledNoWrap>}
        withoutShadow={true}
        withoutBorder={true}
        backgroundColor={"transparent"}
      />
    </StyledTask>
  ));

  return (
    <StyledCard $maxWidth={maxWidth ?? null}>
      <StyledHeader>
        <Spacer y={1.5} />
        <StyledFlex>
          <Spacer x={1.5} />
          <StyledTitle>
            <TextCapitalized>{t("pendingActionsCard.title")}</TextCapitalized>
          </StyledTitle>
          {count > 0 && (
            <>
              <Spacer x={0.5} />
              <Badge
                text={String(count)}
                backgroundColor={colors["colors/text/black"]}
                textColor={colors["colors/text/white"]}
              />
            </>
          )}
          <Spacer x={1.5} />
        </StyledFlex>
        <Spacer y={1.5} />
      </StyledHeader>
      {count === 0 ? emptyState : taskRows}
    </StyledCard>
  );
};

const StyledCard = styled.div<{ $maxWidth: number | null }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
  border-radius: 0.75rem;
  ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth}rem;` : "")}
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
`;

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H2"]};
  color: ${colors["colors/text/black"]};
`;

const StyledTask = styled.div`
  &:not(:last-child) {
    border-bottom: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
  }
`;

const StyledNoWrap = styled.div`
  white-space: nowrap;
`;

const StyledEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StyledCheckCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background-color: ${colors["colors/system/success/success_ultraLight"]};
  & svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledEmptyTitle = styled.div`
  ${typographies["Body/L"]};
  color: ${colors["colors/text/black"]};
`;

const StyledEmptyText = styled.div`
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
  max-width: 30rem;
`;
